(() => {
  const selector = '[data-ps-reveal]';
  const floatingHeaderSelector = '[data-ps-floating-header]';
  const floatingBrandSentinelSelector = '[data-ps-floating-brand-sentinel]';
  const readyClass = 'ps-motion-ready';
  const preparingClass = 'ps-motion-preparing';
  const desktopQuery = window.matchMedia('(min-width: 769px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsObserver = 'IntersectionObserver' in window;
  const pendingElements = new Set();
  const floatingBrandObservers = new Map();
  const storyBackdropObservers = new Map();
  let observer;
  let scrollCheckScheduled = false;

  const getElements = (root = document) => {
    const elements = [];
    if (root.matches?.(selector)) elements.push(root);
    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
    return elements;
  };

  const reveal = (element) => {
    element.classList.add('is-revealed');
    pendingElements.delete(element);
    observer?.unobserve(element);
  };

  const revealAll = (root = document) => {
    getElements(root).forEach(reveal);
  };

  const getFloatingHeaders = (root = document) => {
    const headers = [];
    if (root.matches?.(floatingHeaderSelector)) headers.push(root);
    root.querySelectorAll?.(floatingHeaderSelector).forEach((header) => headers.push(header));
    return headers;
  };

  const initializeFloatingBrands = (root = document) => {
    if (!supportsObserver) return;

    getFloatingHeaders(root).forEach((header) => {
      if (floatingBrandObservers.has(header)) return;

      const section = header.closest('.section-product-story-floating-header');
      const sentinel = section?.querySelector(floatingBrandSentinelSelector);
      if (!sentinel || !header.querySelector('.ps-lp-floating-header__brand')) return;

      const updateBrandVisibility = (isAtPageTop) => {
        header.classList.toggle('is-brand-hidden', !isAtPageTop);
      };
      updateBrandVisibility(sentinel.getBoundingClientRect().top >= 0);

      const floatingBrandObserver = new IntersectionObserver(
        ([entry]) => updateBrandVisibility(entry.isIntersecting),
        { threshold: 1 }
      );
      floatingBrandObserver.observe(sentinel);
      floatingBrandObservers.set(header, floatingBrandObserver);
    });
  };

  const destroyFloatingBrands = (root = document) => {
    getFloatingHeaders(root).forEach((header) => {
      floatingBrandObservers.get(header)?.disconnect();
      floatingBrandObservers.delete(header);
    });
  };

  const getStoryHeroes = (root = document) => {
    const heroes = [];
    if (root.matches?.('.ps-lp-hero')) heroes.push(root);
    root.querySelectorAll?.('.ps-lp-hero').forEach((hero) => heroes.push(hero));
    return heroes;
  };

  const initializeStoryBackdrops = (root = document) => {
    if (!supportsObserver) return;

    getStoryHeroes(root).forEach((hero) => {
      if (storyBackdropObservers.has(hero)) return;

      const story = hero.parentElement?.querySelector('.ps-lp-story');
      if (!story) return;

      const updateBackdrop = (isVisible) => {
        hero.classList.toggle('is-story-backdrop-frosted', isVisible);
      };
      const rect = story.getBoundingClientRect();
      updateBackdrop(rect.top < window.innerHeight - 80 && rect.bottom > 80);

      const storyObserver = new IntersectionObserver(
        ([entry]) => updateBackdrop(entry.isIntersecting),
        { rootMargin: '0px 0px -80px 0px', threshold: 0 }
      );
      storyObserver.observe(story);
      storyBackdropObservers.set(hero, storyObserver);
    });
  };

  const destroyStoryBackdrops = (root = document) => {
    getStoryHeroes(root).forEach((hero) => {
      storyBackdropObservers.get(hero)?.disconnect();
      storyBackdropObservers.delete(hero);
      hero.classList.remove('is-story-backdrop-frosted');
    });
  };

  const createObserver = () => {
    const bottomMargin = desktopQuery.matches ? '-200px' : '0px';
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const passedTriggerLine = entry.rootBounds && entry.boundingClientRect.top < entry.rootBounds.bottom;
          if (entry.isIntersecting || passedTriggerLine) reveal(entry.target);
        });
      },
      { rootMargin: `0px 0px ${bottomMargin} 0px`, threshold: 0 }
    );
  };

  const initialize = (root = document) => {
    if (!supportsObserver || reducedMotionQuery.matches) {
      revealAll(root);
      return;
    }

    if (!observer) observer = createObserver();
    const triggerLine = window.innerHeight - (desktopQuery.matches ? 200 : 0);

    getElements(root).forEach((element) => {
      if (element.classList.contains('is-revealed')) return;
      if (element.getBoundingClientRect().top < triggerLine) reveal(element);
      else {
        pendingElements.add(element);
        observer.observe(element);
      }
    });
  };

  const checkPassedElements = () => {
    const triggerLine = window.innerHeight - (desktopQuery.matches ? 200 : 0);
    pendingElements.forEach((element) => {
      if (element.getBoundingClientRect().top < triggerLine) reveal(element);
    });
    scrollCheckScheduled = false;
  };

  const schedulePassedElementCheck = () => {
    if (scrollCheckScheduled || pendingElements.size === 0) return;
    scrollCheckScheduled = true;
    requestAnimationFrame(checkPassedElements);
  };

  const rebuild = () => {
    observer?.disconnect();
    observer = undefined;
    initialize();
  };

  const start = () => {
    if (supportsObserver && !reducedMotionQuery.matches) {
      document.documentElement.classList.add(readyClass, preparingClass);
    }
    initialize();
    initializeFloatingBrands();
    initializeStoryBackdrops();
    requestAnimationFrame(() => document.documentElement.classList.remove(preparingClass));
  };

  if (supportsObserver && !reducedMotionQuery.matches) {
    document.documentElement.classList.add(readyClass, preparingClass);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  desktopQuery.addEventListener('change', rebuild);
  window.addEventListener('scroll', schedulePassedElementCheck, { passive: true });
  reducedMotionQuery.addEventListener('change', (event) => {
    if (event.matches) {
      document.documentElement.classList.remove(readyClass, preparingClass);
      observer?.disconnect();
      observer = undefined;
      revealAll();
    } else {
      document.documentElement.classList.add(readyClass, preparingClass);
      rebuild();
      requestAnimationFrame(() => document.documentElement.classList.remove(preparingClass));
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    initializeFloatingBrands(event.target);
    initializeStoryBackdrops(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroyFloatingBrands(event.target);
    destroyStoryBackdrops(event.target);
    getElements(event.target).forEach((element) => {
      pendingElements.delete(element);
      observer?.unobserve(element);
    });
  });
})();
